import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { PageHeader } from "@/components/PageHeader";
import { ServiceQueuePanel } from "@/components/queue/ServiceQueuePanel";
import { QuickStockEntryModal } from "@/components/QuickStockEntryModal";
import { getPharmacySettings } from "@/core/admin/establishment-store";
import { syncInvoiceToBilling } from "@/core/billing/billing-sync-api";
import {
  getPrescriptionById,
  isPrescriptionReadyToDispense,
  markPrescriptionDispensed,
  markPrescriptionPharmacistValidated,
} from "@/core/clinical/clinical-store";
import { PRESCRIPTION_STATUS_LABELS } from "@/core/clinical/types";
import { consumePharmacyPrefill } from "@/core/clinical/pharmacy-handoff";
import { notifyPrescriptionValidated } from "@/core/notifications/pharmacy-notify";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { IscApiError } from "@/core/inventory/isc-client";
import {
  isApiOfflineError,
  isInsufficientStockError,
  issueInvoiceLinesAtomically,
  persistProductPrice,
  releaseInvoiceLine,
  releaseInvoiceLines,
  reserveInvoiceLine,
  resolveProductPrice,
} from "@/core/inventory/pharmacy-stock-sync";
import {
  getDraftId,
  nextInvoiceNumber,
  resetDraftId,
  saveValidatedInvoice,
} from "@/core/pharmacy/pharmacy-store";
import type { InvoiceCustomer, InvoiceLine, StockProduct } from "@/core/pharmacy/types";
import { formatMoney } from "@/core/pharmacy/format";
import { getPatientById, listPatients } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { canValidatePharmacyPrescription } from "@/core/permissions";
import { routes } from "@/router/all_routes";

function lineId(): string {
  return `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function PharmacyInvoicePage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const pharmacySettings = useMemo(() => getPharmacySettings(), []);
  const canValidateRx = canValidatePharmacyPrescription(auth);
  const [searchParams] = useSearchParams();
  const scanRef = useRef<HTMLInputElement>(null);
  const scanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScanRef = useRef("");
  const prefillAppliedRef = useRef(false);

  const [products, setProducts] = useState<StockProduct[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [apiOffline, setApiOffline] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanStatus, setScanStatus] = useState<string | null>(null);

  const [patientId, setPatientId] = useState("");
  const [prescriptionId, setPrescriptionId] = useState("");
  const [customer, setCustomer] = useState<InvoiceCustomer>({
    name: "",
    phone: "",
    address: "",
  });
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  const [scanValue, setScanValue] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [priceDraft, setPriceDraft] = useState("");
  const [validating, setValidating] = useState(false);
  const [pharmacistValidating, setPharmacistValidating] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [rxRefreshKey, setRxRefreshKey] = useState(0);

  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [unknownBarcode, setUnknownBarcode] = useState("");

  const reservedProductIds = useMemo(
    () => new Set(lines.map((line) => line.productId)),
    [lines],
  );
  const selectedProduct = products.find((p) => p.id === selectedProductId) ?? null;
  const patients = useMemo(() => listPatients(), []);
  const patientLocked = Boolean(patientId);
  const linkedPrescription = useMemo(() => {
    void rxRefreshKey;
    return prescriptionId ? getPrescriptionById(prescriptionId) : null;
  }, [prescriptionId, rxRefreshKey]);
  const requiresPharmacistStep =
    Boolean(prescriptionId) && pharmacySettings.requirePharmacistValidation;
  const pharmacistValidated =
    !requiresPharmacistStep ||
    (prescriptionId ? isPrescriptionReadyToDispense(prescriptionId) : true);

  const applyPatientToCustomer = useCallback((id: string) => {
    const patient = getPatientById(id);
    if (!patient) return;
    setCustomer({
      name: patientFullName(patient),
      phone: patient.phone,
      address: patient.address,
    });
  }, []);

  const handlePatientChange = (id: string) => {
    setPatientId(id);
    if (id) {
      applyPatientToCustomer(id);
    } else {
      setCustomer({ name: "", phone: "", address: "" });
    }
  };

  useEffect(() => {
    const fromUrl = searchParams.get("patientId");
    if (fromUrl) {
      setPatientId(fromUrl);
      applyPatientToCustomer(fromUrl);
    }
  }, [searchParams, applyPatientToCustomer]);

  const reloadProducts = useCallback(async () => {
    setLoadingProducts(true);
    setApiOffline(false);
    try {
      const list = await pharmacyInventoryApi.listPharmacyProducts();
      setProducts(list.filter((p) => p.status === "ACTIVE"));
    } catch (err) {
      setApiOffline(isApiOfflineError(err));
      setLoadError(
        isApiOfflineError(err)
          ? "API stock inaccessible."
          : err instanceof IscApiError && err.status === 403
            ? "Accès catalogue refusé — compte pharmacien ou clinique requis."
            : err instanceof Error
              ? err.message
              : "Impossible de charger les articles.",
      );
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    void reloadProducts();
    scanRef.current?.focus();
  }, [reloadProducts]);

  useEffect(() => {
    void (async () => {
      const { syncPatientsFromApi } = await import("@/core/patients/patient-store");
      const { syncPharmacyInvoicesFromApi } = await import("@/core/pharmacy/pharmacy-store");
      await syncPatientsFromApi();
      await syncPharmacyInvoicesFromApi();
    })();
  }, []);

  const subtotal = useMemo(
    () => lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0),
    [lines],
  );

  const applyProductToFields = useCallback((product: StockProduct) => {
    setSelectedProductId(product.id);
    const saved = resolveProductPrice(product);
    setPriceDraft(saved !== null ? String(saved) : "");
    setScanStatus(`Article trouvé : ${product.label}`);
    setLoadError(null);
  }, []);

  const addProductToInvoice = useCallback(
    async (product: StockProduct, unitPrice: number, qty = 1) => {
      if (unitPrice <= 0) return;
      await persistProductPrice(product.id, unitPrice);
      const storeId = await pharmacyInventoryApi.getDefaultStoreId();
      if (!storeId) {
        setLoadError("Aucun entrepôt ISC — réservation impossible.");
        return;
      }

      const existing = lines.find((l) => l.productId === product.id);
      if (existing) {
        const newQty = existing.quantity + qty;
        try {
          await reserveInvoiceLine(existing, storeId, newQty);
        } catch (err) {
          setLoadError(
            err instanceof Error ? err.message : "Réservation stock échouée.",
          );
          return;
        }
        setLines((prev) =>
          prev.map((l) =>
            l.productId === product.id ? { ...l, quantity: newQty, unitPrice } : l,
          ),
        );
      } else {
        const newLine: InvoiceLine = {
          id: lineId(),
          productId: product.id,
          label: product.label,
          barcode: product.barcode,
          unitPrice,
          quantity: qty,
        };
        try {
          await reserveInvoiceLine(newLine, storeId, qty);
        } catch (err) {
          setLoadError(
            err instanceof Error ? err.message : "Réservation stock échouée.",
          );
          return;
        }
        setLines((prev) => [...prev, newLine]);
      }
      setSuccessMsg(null);
      setScanStatus(`« ${product.label} » ajouté à la facture (réservé).`);
    },
    [lines],
  );

  useEffect(() => {
    if (loadingProducts || prefillAppliedRef.current || products.length === 0) return;

    const prefill = consumePharmacyPrefill();
    if (!prefill) return;

    prefillAppliedRef.current = true;
    setPatientId(prefill.patientId);
    setPrescriptionId(prefill.prescriptionId);
    applyPatientToCustomer(prefill.patientId);

    void (async () => {
      let added = 0;
      let missingPrice = 0;

      for (const item of prefill.lines) {
        const product = products.find((p) => p.id === item.productId);
        if (!product) continue;

        const price = resolveProductPrice(product) ?? product.sellingPrice ?? 0;
        if (price <= 0) {
          missingPrice += 1;
          continue;
        }

        await addProductToInvoice(product, price, item.quantity);
        added += 1;
      }

      if (added > 0) {
        setSuccessMsg(`Ordonnance chargée — ${added} ligne(s) ajoutée(s) à la facture.`);
      }
      if (missingPrice > 0) {
        setLoadError(
          `${missingPrice} article(s) sans prix officine — complétez dans Articles médicaments.`,
        );
      }
    })();
  }, [loadingProducts, products, applyPatientToCustomer, addProductToInvoice]);

  const runScanLookup = useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed || scanLoading) return;
      if (lastScanRef.current === trimmed) return;

      lastScanRef.current = trimmed;
      setScanLoading(true);
      setScanStatus(null);
      setLoadError(null);

      try {
        const product = await pharmacyInventoryApi.findByBarcode(trimmed);
        setScanValue("");

        if (!product) {
          lastScanRef.current = "";
          setScanStatus(null);
          setUnknownBarcode(trimmed);
          setStockModalOpen(true);
          return;
        }

        applyProductToFields(product);
        const price = resolveProductPrice(product);
        if (price !== null && price > 0) {
          addProductToInvoice(product, price, 1);
        } else {
          setLoadError(`Définissez le prix pour « ${product.label} » puis cliquez Ajouter.`);
          window.setTimeout(() => document.getElementById("ph-price-input")?.focus(), 50);
        }
      } catch (err) {
        lastScanRef.current = "";
        setApiOffline(isApiOfflineError(err));
        setLoadError(
          isApiOfflineError(err)
            ? "API stock inaccessible."
            : err instanceof Error
              ? err.message
              : "Recherche impossible.",
        );
      } finally {
        setScanLoading(false);
        scanRef.current?.focus();
      }
    },
    [addProductToInvoice, applyProductToFields, scanLoading],
  );

  useEffect(() => {
    const trimmed = scanValue.trim();
    if (trimmed.length < 4 || scanLoading) return;

    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    scanTimerRef.current = setTimeout(() => {
      void runScanLookup(trimmed);
    }, 400);

    return () => {
      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    };
  }, [scanValue, scanLoading, runScanLookup]);

  const handleScanKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    void runScanLookup(scanValue);
  };

  const handleSelectChange = (productId: string) => {
    setSelectedProductId(productId);
    setScanStatus(null);
    if (!productId) {
      setPriceDraft("");
      return;
    }
    const product = products.find((p) => p.id === productId);
    if (!product) return;
    const saved = resolveProductPrice(product);
    setPriceDraft(saved !== null ? String(saved) : "");
  };

  const handleSelectAdd = async () => {
    const product = products.find((p) => p.id === selectedProductId);
    if (!product) return;
    const price = Number(priceDraft) || resolveProductPrice(product) || 0;
    if (price <= 0) {
      setLoadError("Saisissez un prix unitaire pour cet article.");
      return;
    }
    await addProductToInvoice(product, price, 1);
    setSelectedProductId("");
    setPriceDraft("");
    setLoadError(null);
    setScanStatus(null);
    lastScanRef.current = "";
  };

  const updateLineQty = async (lineId: string, quantity: number) => {
    const line = lines.find((l) => l.id === lineId);
    if (!line) return;
    const storeId = await pharmacyInventoryApi.getDefaultStoreId();
    if (!storeId) {
      setLoadError("Aucun entrepôt ISC — réservation impossible.");
      return;
    }
    const qty = Math.max(1, quantity);
    try {
      await reserveInvoiceLine(line, storeId, qty);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Réservation stock échouée.");
      return;
    }
    setLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, quantity: qty } : l)));
  };

  const removeLine = async (line: InvoiceLine) => {
    await releaseInvoiceLine(line);
    setLines((prev) => prev.filter((l) => l.id !== line.id));
  };

  const finalizeInvoice = async (stockNote?: string) => {
    const number = nextInvoiceNumber();
    const saved = {
      id: `inv-${Date.now()}`,
      number,
      customer: { ...customer },
      lines: [...lines],
      subtotal,
      validatedAt: new Date().toISOString(),
      ...(patientId ? { patientId } : {}),
      ...(prescriptionId ? { prescriptionId } : {}),
      ...(pharmacySettings.syncBillingOnInvoice
        ? { billingSync: { status: "pending" as const } }
        : {}),
    };
    saveValidatedInvoice(saved);
    if (prescriptionId) {
      markPrescriptionDispensed(prescriptionId);
    }
    if (pharmacySettings.syncBillingOnInvoice) {
      await syncInvoiceToBilling(saved);
    }
    resetDraftId();
    setLines([]);
    setCustomer({ name: "", phone: "", address: "" });
    setPatientId("");
    setPrescriptionId("");
    setSelectedProductId("");
    setPriceDraft("");
    lastScanRef.current = "";
    prefillAppliedRef.current = false;
    const base = `Facture ${number} validée — ${formatMoney(subtotal)} enregistré au tableau de bord.`;
    setSuccessMsg(stockNote ? `${base} ${stockNote}` : base);
    scanRef.current?.focus();
  };

  const handlePharmacistValidate = async () => {
    if (!prescriptionId || !canValidateRx) return;
    if (lines.length === 0) {
      setLoadError("Ajoutez les lignes de l'ordonnance avant validation pharmacien.");
      return;
    }
    setPharmacistValidating(true);
    setLoadError(null);
    try {
      markPrescriptionPharmacistValidated(prescriptionId);
      setRxRefreshKey((k) => k + 1);
      const patient = getPatientById(patientId);
      void notifyPrescriptionValidated({
        prescriptionId,
        patientName: patient ? patientFullName(patient) : customer.name,
      });
      setSuccessMsg("Ordonnance validée par le pharmacien — vous pouvez dispenser.");
    } finally {
      setPharmacistValidating(false);
    }
  };

  const handleValidate = async () => {
    if (pharmacySettings.requirePatientOnInvoice && !patientId) {
      setLoadError("Sélectionnez un patient enregistré — la vente comptoir est désactivée.");
      return;
    }
    if (!patientId && !customer.name.trim()) {
      setLoadError("Sélectionnez un patient enregistré ou saisissez le nom du client.");
      return;
    }
    if (requiresPharmacistStep && !pharmacistValidated) {
      setLoadError("Validez d'abord l'ordonnance (étape pharmacien).");
      return;
    }
    if (lines.length === 0) {
      setLoadError("Ajoutez au moins un produit.");
      return;
    }

    setValidating(true);
    setLoadError(null);
    setSuccessMsg(null);

    try {
      const storeId = await pharmacyInventoryApi.getDefaultStoreId();

      if (storeId) {
        try {
          await issueInvoiceLinesAtomically(lines, storeId, getDraftId());
        } catch (err) {
          if (isInsufficientStockError(err)) {
            setLoadError(
              `Stock insuffisant — validation annulée. ${err instanceof Error ? err.message : ""}`.trim(),
            );
            return;
          }
          if (isApiOfflineError(err)) {
            setLoadError(
              "API stock inaccessible — impossible de valider sans sortie stock ISC.",
            );
            return;
          }
          throw err;
        }
      } else {
        setLoadError("Aucun entrepôt ISC configuré — validation annulée.");
        return;
      }

      await finalizeInvoice();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Validation impossible");
    } finally {
      setValidating(false);
    }
  };

  const invoiceDate = new Date().toLocaleDateString("fr-FR");

  return (
    <div>
      <PageHeader
        title="Nouvelle facture"
        description="Vente comptoir — scan ou sélection article à gauche, client et validation à droite."
        actions={
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(routes.tenantDashboard)}
          >
            Tableau de bord
          </button>
        }
      />

      <ServiceQueuePanel
        serviceId="pharmacy"
        defaultUnitName="Pharmacie"
        enableCallNext
        title="Salle d'attente — officine"
      />

      {successMsg && <div className="alert alert-success">{successMsg}</div>}
      {loadError && (
        <div className="alert alert-warning py-2 mb-2">
          {loadError}
          {apiOffline && (
            <span className="ms-1 small">
              Lancez <code>pnpm dev:api</code> dans <code>itaas-inventory</code>.
            </span>
          )}
        </div>
      )}

      <div className="ph-pos-layout">
        <div className="ph-pos-main">
          <div className="ph-invoice-sheet ph-pos-scan-panel">
            <div className="ph-invoice-section-title mb-2">Ajouter un article</div>

            <label className="form-label small mb-1 fw-semibold" htmlFor="ph-pos-scan">
              Scanner le code-barres
            </label>
            <div className="position-relative mb-3">
              <input
                ref={scanRef}
                id="ph-pos-scan"
                className={`form-control ph-pos-scan-input ${scanLoading ? "pe-5" : ""}`}
                placeholder="Scannez ici — recherche automatique…"
                value={scanValue}
                onChange={(e) => {
                  lastScanRef.current = "";
                  setScanValue(e.target.value);
                  setScanStatus(null);
                }}
                onKeyDown={handleScanKeyDown}
                disabled={scanLoading}
                autoComplete="off"
              />
              {scanLoading && (
                <span
                  className="position-absolute top-50 end-0 translate-middle-y me-3 ph-scan-spinner"
                  aria-hidden
                />
              )}
            </div>
            {scanLoading && (
              <p className="small text-primary mb-2">Recherche dans le stock…</p>
            )}
            {scanStatus && !scanLoading && (
              <p className="small text-success mb-3">{scanStatus}</p>
            )}

            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-6">
                <label className="form-label small mb-1">Sélectionner un article</label>
                <select
                  className="form-select form-select-sm"
                  value={selectedProductId}
                  onChange={(e) => handleSelectChange(e.target.value)}
                  disabled={loadingProducts}
                >
                  <option value="">
                    {loadingProducts ? "Chargement…" : "— Choisir dans le stock —"}
                  </option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                      {p.barcode ? ` (${p.barcode})` : ""}
                      {reservedProductIds.has(p.id) ? " · brouillon" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-sm-6 col-md-3">
                <label className="form-label small mb-1">Prix unitaire</label>
                <input
                  id="ph-price-input"
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control form-control-sm"
                  placeholder="0.00"
                  value={priceDraft}
                  onChange={(e) => setPriceDraft(e.target.value)}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-3">
                <button
                  type="button"
                  className="btn btn-sm btn-primary w-100"
                  onClick={() => void handleSelectAdd()}
                  disabled={!selectedProductId}
                >
                  Ajouter
                </button>
              </div>
            </div>

            {selectedProduct && (
              <div className="ph-selected-product mt-3 mb-0">
                <span className="fw-semibold">{selectedProduct.label}</span>
                {selectedProduct.barcode && (
                  <span className="text-muted small ms-2">({selectedProduct.barcode})</span>
                )}
              </div>
            )}
          </div>

          <div className="ph-invoice-sheet ph-pos-lines-panel">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="ph-invoice-section-title mb-0">
                Lignes ({lines.length})
              </div>
              {lines.length > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm text-danger p-0"
                  onClick={() => {
                    void releaseInvoiceLines(lines).then(() => {
                      setLines([]);
                      setSuccessMsg(null);
                      setScanStatus(null);
                    });
                  }}
                >
                  Vider le brouillon
                </button>
              )}
            </div>

            <div className="ph-pos-lines-wrap ph-diag-table">
              <table className="table table-sm ph-invoice-table mb-0">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th className="text-end">Prix</th>
                    <th className="text-end" style={{ width: "4.5rem" }}>
                      Qté
                    </th>
                    <th className="text-end">Total</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-5">
                        Aucune ligne — scannez ou sélectionnez un article.
                      </td>
                    </tr>
                  ) : (
                    lines.map((line) => (
                      <tr key={line.id} className="ph-diag-row">
                        <td data-label="Produit">
                          <span className="fw-medium">{line.label}</span>
                          {line.barcode && (
                            <span className="d-block small text-muted">{line.barcode}</span>
                          )}
                        </td>
                        <td data-label="Prix" className="text-end">
                          {formatMoney(line.unitPrice)}
                        </td>
                        <td data-label="Qté" className="text-end">
                          <input
                            type="number"
                            min="1"
                            className="form-control form-control-sm text-end"
                            value={line.quantity}
                            onChange={(e) =>
                              void updateLineQty(line.id, Number(e.target.value) || 1)
                            }
                          />
                        </td>
                        <td data-label="Total" className="text-end fw-semibold">
                          {formatMoney(line.unitPrice * line.quantity)}
                        </td>
                        <td data-label="Action" className="text-end">
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0"
                            onClick={() => void removeLine(line)}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <aside className="ph-pos-side">
          <div className="ph-invoice-sheet ph-pos-checkout">
            <div className="ph-pos-checkout-head">
              <div>
                <div className="ph-invoice-brand">ITaaS Hospitol</div>
                <div className="ph-invoice-sub">Facture officine</div>
              </div>
              <div className="text-end">
                <div className="ph-invoice-title">FACTURE</div>
                <div className="small text-muted">Brouillon</div>
                <div className="small">{invoiceDate}</div>
              </div>
            </div>

            <div className="ph-pos-checkout-section">
              <div className="ph-invoice-section-title">
                {patientLocked ? "Patient enregistré" : "Client / patient"}
              </div>

              <label className="form-label small mb-1" htmlFor="ph-patient-select">
                Patient (recommandé)
              </label>
              <select
                id="ph-patient-select"
                className="form-select form-select-sm mb-2"
                value={patientId}
                onChange={(e) => handlePatientChange(e.target.value)}
                required={pharmacySettings.requirePatientOnInvoice}
              >
                {!pharmacySettings.requirePatientOnInvoice && (
                  <option value="">— Client ponctuel —</option>
                )}
                {pharmacySettings.requirePatientOnInvoice && (
                  <option value="">— Sélectionnez un patient —</option>
                )}
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {patientFullName(p)} ({p.fileNumber})
                  </option>
                ))}
              </select>

              {prescriptionId && linkedPrescription && (
                <div className="alert alert-primary py-2 px-2 small mb-2">
                  Ordonnance {prescriptionId.slice(0, 12)}… —{" "}
                  <strong>{PRESCRIPTION_STATUS_LABELS[linkedPrescription.status]}</strong>
                  {requiresPharmacistStep && !pharmacistValidated && (
                    <span className="d-block text-warning mt-1">
                      Validation pharmacien requise
                    </span>
                  )}
                </div>
              )}

              <input
                className="form-control form-control-sm mb-2"
                placeholder="Nom complet *"
                value={customer.name}
                disabled={patientLocked || pharmacySettings.requirePatientOnInvoice}
                onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
              />
              <input
                className="form-control form-control-sm mb-2"
                placeholder="Téléphone"
                value={customer.phone}
                disabled={patientLocked}
                onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))}
              />
              <textarea
                className="form-control form-control-sm"
                rows={2}
                placeholder="Adresse"
                value={customer.address}
                disabled={patientLocked}
                onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))}
              />
              {patientLocked && (
                <p className="small text-muted mb-0 mt-2">
                  Identité verrouillée — changez le patient pour saisie manuelle.
                </p>
              )}
            </div>

            <div className="ph-pos-checkout-totals">
              <div className="d-flex justify-content-between small text-muted">
                <span>{lines.length} article(s)</span>
                <span>Réservé en stock</span>
              </div>
              <div className="d-flex justify-content-between ph-invoice-grand mt-2">
                <span>Total à payer</span>
                <strong>{formatMoney(subtotal)}</strong>
              </div>
            </div>

            <div className="ph-pos-checkout-actions">
              {requiresPharmacistStep && canValidateRx && !pharmacistValidated && (
                <button
                  type="button"
                  className="btn btn-warning w-100 mb-2"
                  onClick={() => void handlePharmacistValidate()}
                  disabled={pharmacistValidating || lines.length === 0}
                >
                  {pharmacistValidating ? "Validation…" : "Valider pharmacien"}
                </button>
              )}
              <button
                type="button"
                className="btn btn-success w-100"
                onClick={() => void handleValidate()}
                disabled={
                  validating ||
                  lines.length === 0 ||
                  (requiresPharmacistStep && !pharmacistValidated)
                }
                title={
                  requiresPharmacistStep && !pharmacistValidated
                    ? "Validation pharmacien requise"
                    : undefined
                }
              >
                {validating ? "Dispensation…" : "Dispenser / Valider facture"}
              </button>
            </div>
          </div>
        </aside>
      </div>

      <QuickStockEntryModal
        open={stockModalOpen}
        barcode={unknownBarcode}
        onClose={() => {
          setStockModalOpen(false);
          lastScanRef.current = "";
          scanRef.current?.focus();
        }}
        onCreated={(product, price) => {
          setProducts((prev) => [...prev, product]);
          applyProductToFields(product);
          addProductToInvoice(product, price, 1);
        }}
      />
    </div>
  );
}
